// Display rules for Mission Control's fly previews page. Pure, so each rule the
// page shows is unit-tested without rendering anything.

// eslint-disable-next-line relay/no-future-added-value
export type FlyPreviewStatus = 'RUNNING' | 'STOPPED' | 'STARTING' | 'DEGRADED' | '%future added value';

export interface FlyPreviewRow {
	readonly id: string;
	readonly slug: string;
	readonly branchName: string | null | undefined;
	readonly webUrl: string;
	readonly status: FlyPreviewStatus;
	readonly machinesTotal: number;
	readonly machinesRunning: number;
	readonly lastUsedAt: string | null | undefined;
	// False when usage metrics could not be read; lastUsedAt is then unknown rather than absent.
	readonly lastUsedKnown: boolean;
	readonly createdAt: string | null | undefined;
	// Null on stacks not deployed since deploy times started being recorded.
	readonly deployedAt: string | null | undefined;
}

export type LastUsed = Pick<FlyPreviewRow, 'lastUsedAt' | 'lastUsedKnown'>;

// What a row shows. STOPPING is client-only: fly carries out a stop asynchronously, so the Stop
// mutation usually returns a stack that still reads RUNNING, and the row covers the gap itself.
export type RowStatus = FlyPreviewStatus | 'STOPPING';

export const POLL_INTERVAL_MS = 5000;
// How long a row waits for a stop to land before it shows the server's status again, so a stack
// with a machine that refused to stop falls back to DEGRADED and its buttons.
export const STOP_WATCH_MS = 2 * 60 * 1000;
export const STOP_CONFIRM_WINDOW_MS = 60 * 60 * 1000;
// How far back "Last used" looks; must match LAST_USED_WINDOW_DAYS in graphql's FlyPreviewLogic.js.
export const LAST_USED_WINDOW_DAYS = 7;
export const NOT_CONFIGURED_MESSAGE = "Fly previews aren't configured in this environment.";

export interface PreviewActions {
	readonly start: boolean;
	readonly stop: boolean;
}

const STATUS_TEXT: Record<string, string> = {
	RUNNING: 'Running',
	STOPPED: 'Stopped',
	STARTING: 'Starting…',
	STOPPING: 'Stopping…',
	DEGRADED: 'Degraded',
};

export const previewLabel = (row: FlyPreviewRow): string => row.branchName || row.slug;

// Which of a row's Start and Stop buttons can be pressed; both are always shown. A degraded stack
// can be finished off with Start or shut down with Stop, since it may be crash-looping and costing
// money. Neither is pressable while a start or stop is already under way.
export function actionsFor(status: RowStatus): PreviewActions {
	if (status === 'RUNNING') {
		return {start: false, stop: true};
	}
	if (status === 'STARTING' || status === 'STOPPING') {
		return {start: false, stop: false};
	}
	if (status === 'DEGRADED') {
		return {start: true, stop: true};
	}
	return {start: true, stop: false};
}

export const statusText = (status: RowStatus): string => STATUS_TEXT[status] ?? status;

// A row waiting on its own Stop shows STOPPING until fly reports the stack stopped.
export const rowStatus = (row: FlyPreviewRow, stopping: boolean): RowStatus =>
	stopping && row.status !== 'STOPPED' ? 'STOPPING' : row.status;

export function statusLabel(row: FlyPreviewRow, status: RowStatus = row.status): string {
	const text = statusText(status);
	const partlyUp = row.machinesRunning > 0 && row.machinesRunning < row.machinesTotal;
	return partlyUp ? `${text} · ${row.machinesRunning} of ${row.machinesTotal} up` : text;
}

// Stopping can't lose data, but it can cut off a colleague mid-test. When usage couldn't be read
// there is no telling, so ask.
export function needsStopConfirm(row: LastUsed, now: number): boolean {
	if (!row.lastUsedKnown) {
		return true;
	}
	return Boolean(row.lastUsedAt) && now - Date.parse(row.lastUsedAt as string) < STOP_CONFIRM_WINDOW_MS;
}

// "just now", "12 min ago", "3h ago" or "2d ago" for a past ISO-8601 time.
function timeAgo(when: string, now: number): string {
	const minutes = Math.floor((now - Date.parse(when)) / 60000);
	if (minutes < 1) {
		return 'just now';
	}
	if (minutes < 60) {
		return `${minutes} min ago`;
	}
	const hours = Math.floor(minutes / 60);
	if (hours < 24) {
		return `${hours}h ago`;
	}
	return `${Math.floor(hours / 24)}d ago`;
}

export function describeLastUsed({lastUsedAt, lastUsedKnown}: LastUsed, now: number): string {
	if (!lastUsedKnown) {
		return 'Unknown';
	}
	if (!lastUsedAt) {
		return `No use in ${LAST_USED_WINDOW_DAYS} days`;
	}
	return timeAgo(lastUsedAt, now);
}

// A past ISO-8601 time as "just now", "12 min ago", "3h ago" or "2d ago"; a dash when absent.
export function describeWhen(when: string | null | undefined, now: number): string {
	return when ? timeAgo(when, now) : '—';
}

// Returns a new set, so it can be handed straight to a useState setter.
export function withMember(set: ReadonlySet<string>, member: string, present: boolean): ReadonlySet<string> {
	if (set.has(member) === present) {
		return set;
	}
	const next = new Set(set);
	if (present) {
		next.add(member);
	} else {
		next.delete(member);
	}
	return next;
}

// Alphabetical by the label the row shows, so a row keeps its place when its status changes.
export function sortRows<T extends FlyPreviewRow>(rows: ReadonlyArray<T>): T[] {
	return [...rows].sort((a, b) => previewLabel(a).localeCompare(previewLabel(b)) || a.slug.localeCompare(b.slug));
}
